import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { fetchSettings, adminUpdateSettings, adminUploadFont, adminDeleteFont } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useLang } from "@/context/LanguageContext";
import { fontOptionsFor, LATIN_SERIF } from "@/components/TypographyLoader";
import {
  TG_GROUPS, TG_ROLES, TG_FONT_DEFAULTS, TG_SCOPES,
  TG_LANG_ROLES, TG_LANG_ROLE_KEYS, fontsForLang, scriptFallbackFor,
} from "@/lib/typographyRoles";
import { Loader2, Save, RotateCcw, Type, X, Layers, Upload, Trash2, Globe, Maximize2 } from "lucide-react";

const WEIGHTS = [["", "Default"], ["400", "Regular (400)"], ["500", "Medium (500)"], ["600", "Semibold (600)"], ["700", "Bold (700)"], ["800", "Extra bold (800)"]];
const SIZES = [["0.9", "Compact"], ["1", "Normal"], ["1.1", "Comfortable"], ["1.2", "Large"], ["1.35", "Extra large"]];
const SIZE_OPTS = [["", "Default"], ["0.75", "XS (12px)"], ["0.8125", "13px"], ["0.875", "Small (14px)"], ["0.9375", "15px"], ["1", "Normal (16px)"], ["1.0625", "17px"], ["1.125", "Large (18px)"], ["1.25", "XL (20px)"], ["1.375", "22px"], ["1.5", "Huge (24px)"], ["1.75", "28px"], ["2", "32px"]];

const DEFAULTS = { ...TG_FONT_DEFAULTS, heading_weight: "", reading_scale: "1" };

// One-tap size presets — scale every role's size for a language in a single click.
const SIZE_PRESETS = {
  Comfortable: { heading: "1.5", reading: "1.125", body: "1", explainer: "1", note: "0.875", nav: "0.9375", nav_category: "0.875", eyebrow: "0.75", brand: "1.25", button: "0.9375", footer: "0.875" },
  Large: { heading: "1.75", reading: "1.25", body: "1.0625", explainer: "1.125", note: "0.9375", nav: "1", nav_category: "0.9375", eyebrow: "0.8125", brand: "1.375", button: "1", footer: "0.9375" },
  "Extra-large": { heading: "2", reading: "1.375", body: "1.125", explainer: "1.25", note: "1", nav: "1.0625", nav_category: "1", eyebrow: "0.875", brand: "1.5", button: "1.0625", footer: "1" },
};

// Small native sample strings so the per-language preview reads in-script.
const LANG_SAMPLE = {
  ta: { eyebrow: "கதை", heading: "தர்மத்தின் பாதை", body: "பக்தியும் உறுதியும் நிறைந்த ஓர் அழியாத கதை.", note: "குறிப்பு: ஸ்ரீமத் பாகவதம்", button: "வழிகாட்டியைத் திற" },
  te: { eyebrow: "కథ", heading: "ధర్మ మార్గం", body: "భక్తి మరియు సంకల్పంతో నిండిన ఒక అమర గాథ.", note: "సూచన: శ్రీమద్ భాగవతం", button: "మార్గదర్శిని తెరవండి" },
  bn: { eyebrow: "কথা", heading: "ধর্মের পথ", body: "ভক্তি ও সংকল্পে পূর্ণ এক অমর কাহিনি।", note: "সূত্র: শ্রীমদ্ভাগবত", button: "নির্দেশিকা খুলুন" },
  kn: { eyebrow: "ಕಥೆ", heading: "ಧರ್ಮದ ಮಾರ್ಗ", body: "ಭಕ್ತಿ ಮತ್ತು ಸಂಕಲ್ಪದಿಂದ ತುಂಬಿದ ಅಮರ ಕಥೆ.", note: "ಆಕರ: ಶ್ರೀಮದ್ ಭಾಗವತ", button: "ಮಾರ್ಗದರ್ಶಿ ತೆರೆಯಿರಿ" },
  ml: { eyebrow: "കഥ", heading: "ധർമ്മത്തിന്റെ പാത", body: "ഭക്തിയും നിശ്ചയദാർഢ്യവും നിറഞ്ഞ ഒരു അമര കഥ.", note: "സ്രോതസ്സ്: ശ്രീമദ് ഭാഗവതം", button: "വഴികാട്ടി തുറക്കുക" },
  gu: { eyebrow: "કથા", heading: "ધર્મનો માર્ગ", body: "ભક્તિ અને સંકલ્પથી ભરેલી એક અમર ગાથા.", note: "સ્રોત: શ્રીમદ્ ભાગવત", button: "માર્ગદર્શિકા ખોલો" },
  pa: { eyebrow: "ਕਥਾ", heading: "ਧਰਮ ਦਾ ਰਾਹ", body: "ਭਗਤੀ ਅਤੇ ਸੰਕਲਪ ਨਾਲ ਭਰੀ ਇੱਕ ਅਮਰ ਕਥਾ।", note: "ਸਰੋਤ: ਸ੍ਰੀਮਦ ਭਾਗਵਤ", button: "ਗਾਈਡ ਖੋਲ੍ਹੋ" },
  or: { eyebrow: "କଥା", heading: "ଧର୍ମର ପଥ", body: "ଭକ୍ତି ଓ ସଂକଳ୍ପରେ ପରିପୂର୍ଣ୍ଣ ଏକ ଅମର କାହାଣୀ।", note: "ଉତ୍ସ: ଶ୍ରୀମଦ୍ ଭାଗବତ", button: "ଗାଇଡ୍ ଖୋଲନ୍ତୁ" },
  mr: { eyebrow: "कथा", heading: "धर्माचा मार्ग", body: "भक्ती आणि निश्चयाने भरलेली एक अमर कथा.", note: "संदर्भ: श्रीमद्भागवत", button: "मार्गदर्शिका उघडा" },
  ur: { eyebrow: "کتھا", heading: "دھرم کا راستہ", body: "عقیدت اور عزم سے بھری ایک لافانی داستان۔", note: "ماخذ: شریمد بھاگوت", button: "گائیڈ کھولیں" },
};
const sampleFor = (code) => LANG_SAMPLE[code] || { eyebrow: "Katha", heading: "The Path of Dharma", body: "An immortal tale of devotion and resolve.", note: "Reference: Srimad Bhagavatam", button: "Open the guide" };

// Load previewable families once so dropdowns/samples render in the real font.
function usePreviewFonts(langCode) {
  // Base English/Devanagari roles.
  useEffect(() => {
    const all = [...new Set(TG_ROLES.flatMap((r) => fontOptionsFor(r)))];
    const parts = all.map((f) => `family=${f.replace(/\s+/g, "+")}:wght@400;600`);
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.id = "dh-typography-preview";
    link.href = `https://fonts.googleapis.com/css2?${parts.join("&")}&display=swap`;
    if (!document.getElementById(link.id)) document.head.appendChild(link);
  }, []);
  // The active language's script fonts (so its dropdown + preview render correctly).
  useEffect(() => {
    if (!langCode || langCode === "default") return;
    const fams = fontsForLang(langCode);
    const parts = fams.map((f) => `family=${f.replace(/\s+/g, "+")}:wght@400;600`);
    const ID = "dh-typography-preview-lang";
    const existing = document.getElementById(ID);
    if (existing) existing.remove();
    const link = document.createElement("link");
    link.rel = "stylesheet"; link.id = ID;
    link.href = `https://fonts.googleapis.com/css2?${parts.join("&")}&display=swap`;
    document.head.appendChild(link);
  }, [langCode]);
}

export default function AdminTypography() {
  const { reload, customFonts, reloadFonts } = useSettings();
  const { languages } = useLang();
  const [doc, setDoc] = useState(null); // { ...globalFlat, pages:{...}, langs:{...} }
  const [scope, setScope] = useState("global");
  const [mode, setMode] = useState("default"); // "default" | <language code>
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState("hi");
  const [uploading, setUploading] = useState(false);
  const [newFamily, setNewFamily] = useState("");
  const fileRef = useRef(null);
  usePreviewFonts(mode);

  useEffect(() => {
    fetchSettings()
      .then((d) => setDoc(d.typography || {}))
      .catch(() => setDoc({}));
  }, []);

  // Enabled languages that get their own control set (en + hi use the default tab).
  const langTabs = (languages || []).filter((l) => l.code !== "en" && l.code !== "hi");
  const custom = customFonts || [];
  const isLangMode = mode !== "default";
  const isGlobal = scope === "global";

  // Active values for the current mode/scope.
  const values = !doc ? {} : isLangMode
    ? { ...((doc.langs || {})[mode] || {}) }
    : isGlobal
      ? { ...DEFAULTS, ...Object.fromEntries(Object.entries(doc).filter(([k]) => k !== "pages" && k !== "langs")) }
      : { ...(doc.pages?.[scope] || {}) };

  const set = (k, v) => setDoc((d) => {
    if (isLangMode) return { ...d, langs: { ...(d.langs || {}), [mode]: { ...((d.langs || {})[mode] || {}), [k]: v } } };
    if (isGlobal) return { ...d, [k]: v };
    return { ...d, pages: { ...(d.pages || {}), [scope]: { ...(d.pages?.[scope] || {}), [k]: v } } };
  });

  const save = async () => {
    setSaving(true);
    try {
      await adminUpdateSettings({ typography: doc });
      await reload();
      const where = isLangMode ? (langTabs.find((l) => l.code === mode)?.native || langTabs.find((l) => l.code === mode)?.label || mode)
        : isGlobal ? "all pages" : TG_SCOPES.find((s) => s.key === scope)?.label;
      toast.success("Typography saved", { description: isLangMode ? `Applied platform-wide for ${where}.` : `Applied to: ${where}.` });
    } catch (err) {
      toast.error("Could not save", { description: err.response?.data?.detail || err.message });
    } finally { setSaving(false); }
  };

  const resetAll = () => setDoc((d) => {
    if (isLangMode) return { ...d, langs: { ...(d.langs || {}), [mode]: {} } };
    if (isGlobal) return { ...DEFAULTS, pages: d.pages || {}, langs: d.langs || {} };
    return { ...d, pages: { ...(d.pages || {}), [scope]: {} } };
  });
  const resetRole = (key) => setDoc((d) => {
    const patch = { [key]: isGlobal && !isLangMode ? (TG_FONT_DEFAULTS[key] || "") : "", [`${key}_size`]: "", [`${key}_color`]: "" };
    if (isLangMode) return { ...d, langs: { ...(d.langs || {}), [mode]: { ...((d.langs || {})[mode] || {}), ...patch } } };
    if (isGlobal) return { ...d, ...patch };
    return { ...d, pages: { ...(d.pages || {}), [scope]: { ...(d.pages?.[scope] || {}), ...patch } } };
  });

  const roleChanged = (key) => isGlobal && !isLangMode
    ? ((values[key] || "") !== (TG_FONT_DEFAULTS[key] || "") || (values[`${key}_size`] || "") || (values[`${key}_color`] || ""))
    : ((values[key] || "") || (values[`${key}_size`] || "") || (values[`${key}_color`] || ""));

  // Effective value for preview (page scope falls back to Global, then default).
  const eff = (key, suffix = "") => {
    const k = suffix ? `${key}_${suffix}` : key;
    if (values[k]) return values[k];
    if (!isGlobal && !isLangMode && doc?.[k]) return doc[k];
    return suffix ? "" : (!isLangMode ? (TG_FONT_DEFAULTS[key] || "") : "");
  };
  const rStyle = (key, deva) => {
    const fam = eff(key);
    return {
      fontFamily: fam ? `'${fam}', ${deva ? "'Noto Sans Devanagari',sans-serif" : "'Noto Serif Devanagari',serif"}` : undefined,
      fontSize: eff(key, "size") ? `${parseFloat(eff(key, "size"))}rem` : undefined,
      color: eff(key, "color") || undefined,
    };
  };
  // Per-language preview style.
  const lStyle = (key) => {
    const fam = values[key] || "";
    return {
      fontFamily: fam ? `'${fam}', ${scriptFallbackFor(mode)}` : scriptFallbackFor(mode),
      fontSize: values[`${key}_size`] ? `${parseFloat(values[`${key}_size`])}rem` : undefined,
      color: values[`${key}_color`] || undefined,
    };
  };

  const uploadFont = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const f = await adminUploadFont(file, newFamily.trim());
      await reloadFonts();
      setNewFamily("");
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Font uploaded", { description: `“${f.family}” is now available in every font menu.` });
    } catch (err) {
      toast.error("Upload failed", { description: err.response?.data?.detail || err.message });
    } finally { setUploading(false); }
  };
  // Per-language one-tap size presets.
  const applyPreset = (sizes) => setDoc((d) => {
    const cur = { ...((d.langs || {})[mode] || {}) };
    Object.entries(sizes).forEach(([k, v]) => { cur[`${k}_size`] = v; });
    return { ...d, langs: { ...(d.langs || {}), [mode]: cur } };
  });
  const clearSizes = () => setDoc((d) => {
    const cur = { ...((d.langs || {})[mode] || {}) };
    TG_LANG_ROLE_KEYS.forEach((k) => { delete cur[`${k}_size`]; });
    return { ...d, langs: { ...(d.langs || {}), [mode]: cur } };
  });
  const activePreset = Object.entries(SIZE_PRESETS).find(([, sizes]) =>
    Object.entries(sizes).every(([k, v]) => (values[`${k}_size`] || "") === v))?.[0] || null;

  const removeFont = async (f) => {
    if (!window.confirm(`Remove the font “${f.family}”? Any text using it will fall back to the default.`)) return;
    try {
      await adminDeleteFont(f.id);
      await reloadFonts();
      toast.success(`Removed “${f.family}”.`);
    } catch (err) { toast.error("Could not remove font", { description: err.response?.data?.detail || err.message }); }
  };

  if (!doc) return <div className="flex items-center gap-2 py-16 text-ink-3"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  const activeLang = langTabs.find((l) => l.code === mode);

  return (
    <div data-testid="admin-typography">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="inline-flex items-center gap-2 font-serif-latin text-3xl text-ink"><Type className="h-6 w-6 text-saffron" /> Typography</h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-3">Control the <span className="text-ink-2">font, size and colour</span> of every kind of text. Use the <span className="text-ink-2">language tabs</span> to give each language its own fonts &amp; sizes across the whole site, and upload your own font files to use fonts beyond the built-in list. Leave a control on “Default” to inherit.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={resetAll} data-testid="typography-reset" className="inline-flex items-center gap-1.5 border border-line px-3 py-2 text-sm text-ink-2 transition-colors hover:border-ink hover:text-ink"><RotateCcw className="h-4 w-4" /> Reset {isLangMode ? "language" : isGlobal ? "all" : "page"}</button>
          <button onClick={save} disabled={saving} data-testid="typography-save" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </button>
        </div>
      </div>

      {/* Language tabs */}
      <div className="mb-5 border border-line bg-surface p-4" data-testid="typography-lang-bar">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-2"><Globe className="h-4 w-4 text-saffron" /> Language</span>
          <button onClick={() => setMode("default")} data-testid="typography-lang-default"
            className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${mode === "default" ? "border-saffron bg-[var(--saffron)] text-white" : "border-line text-ink-2 hover:border-saffron hover:text-saffron"}`}>
            English &amp; हिन्दी
          </button>
          {langTabs.map((l) => (
            <button key={l.code} onClick={() => setMode(l.code)} data-testid={`typography-lang-${l.code}`}
              className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${mode === l.code ? "border-saffron bg-[var(--saffron)] text-white" : "border-line text-ink-2 hover:border-saffron hover:text-saffron"}`}>
              {l.native || l.label} {l.draft && <span className="opacity-60">· draft</span>}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-ink-3">
          {isLangMode
            ? <>These fonts &amp; sizes apply <span className="text-ink-2">only when visitors read the site in {activeLang?.native || activeLang?.label}</span>, across every page. Empty controls keep the browser default for that script.</>
            : <>The base English &amp; Hindi (Devanagari) typography. Pick a <span className="text-ink-2">scope</span> below to keep changes to one area of the site.</>}
        </p>
      </div>

      {/* Custom fonts uploader */}
      <div className="mb-8 border border-line bg-surface p-4" data-testid="typography-fonts">
        <p className="inline-flex items-center gap-1.5 font-serif-latin text-lg text-ink"><Upload className="h-4 w-4 text-saffron" /> Your fonts</p>
        <p className="mb-3 text-xs text-ink-3">Upload a font file (.woff2, .woff, .ttf or .otf). It appears in every font menu below — for all roles and all languages — and renders across the whole site.</p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">Font name (optional)</span>
            <input value={newFamily} onChange={(e) => setNewFamily(e.target.value)} data-testid="typography-font-name" placeholder="e.g. My Brand Serif" className="w-56 border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
          </label>
          <input ref={fileRef} type="file" accept=".woff2,.woff,.ttf,.otf,font/woff2,font/woff,font/ttf,font/otf" data-testid="typography-font-file"
            onChange={(e) => uploadFont(e.target.files?.[0])}
            className="text-sm text-ink-2 file:mr-3 file:cursor-pointer file:border file:border-line file:bg-parchment file:px-4 file:py-2 file:text-sm file:text-ink hover:file:border-saffron" />
          {uploading && <span className="inline-flex items-center gap-1.5 text-sm text-ink-3"><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</span>}
        </div>
        {custom.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="typography-font-list">
            {custom.map((f) => (
              <span key={f.id} data-testid={`typography-font-chip-${f.id}`} className="inline-flex items-center gap-2 border border-line bg-parchment px-3 py-1.5 text-sm text-ink">
                <span style={{ fontFamily: `'${f.family}'` }}>{f.family}</span>
                <button onClick={() => removeFont(f)} title="Remove" className="text-ink-3 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Scope / page filter — default mode only */}
      {!isLangMode && (
        <div className="mb-8 border border-line bg-surface p-4" data-testid="typography-scope-bar">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-2"><Layers className="h-4 w-4 text-saffron" /> Applies to</span>
            {TG_SCOPES.map((s) => (
              <button key={s.key} onClick={() => setScope(s.key)} data-testid={`typography-scope-${s.key}`}
                className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${scope === s.key ? "border-saffron bg-[var(--saffron)] text-white" : "border-line text-ink-2 hover:border-saffron hover:text-saffron"}`}>
                {s.label.replace(" — all pages", "")}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-ink-3">{TG_SCOPES.find((s) => s.key === scope)?.help} {!isGlobal && <span className="text-ink-2">Empty controls here inherit the Global set.</span>}</p>
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr]">
        <div className="space-y-8">
          {isLangMode ? (
            /* ── Per-language role editor ── */
            <div>
              <h2 className="font-serif-latin text-xl text-ink">{activeLang?.native || activeLang?.label} typography</h2>
              <p className="mb-3 text-xs text-ink-3">Choose a font, size and colour for each kind of text. Font options are tuned for this language's script, plus any fonts you've uploaded.</p>
              <div className="mb-4 border border-line bg-surface p-3" data-testid="typography-size-presets">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-2"><Maximize2 className="h-4 w-4 text-saffron" /> One-tap size</span>
                  {Object.keys(SIZE_PRESETS).map((name) => (
                    <button key={name} onClick={() => applyPreset(SIZE_PRESETS[name])} data-testid={`typography-preset-${name.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${activePreset === name ? "border-saffron bg-[var(--saffron)] text-white" : "border-line text-ink-2 hover:border-saffron hover:text-saffron"}`}>
                      {name}
                    </button>
                  ))}
                  <button onClick={clearSizes} data-testid="typography-preset-clear" className="rounded-full border border-line px-3 py-1.5 text-sm text-ink-3 transition-colors hover:border-ink hover:text-ink">Clear sizes</button>
                </div>
                <p className="mt-2 text-xs text-ink-3">Scales every text size for {activeLang?.native || activeLang?.label} in one click. You can still fine-tune any single size below.</p>
              </div>
              <div className="space-y-4">
                {TG_LANG_ROLES.map((role) => {
                  const opts = fontsForLang(mode);
                  const color = values[`${role.key}_color`] || "";
                  return (
                    <div key={role.key} className="border border-line bg-surface p-4" data-testid={`typography-lang-role-${role.key}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-serif-latin text-lg text-ink">{role.label}</p>
                          <p className="text-xs text-ink-3">{role.help}</p>
                        </div>
                        {roleChanged(role.key) && <button onClick={() => resetRole(role.key)} className="shrink-0 text-[11px] text-saffron hover:underline">Reset</button>}
                      </div>
                      <div className="mt-3 grid gap-3 sm:grid-cols-[1.5fr_1fr_auto]">
                        <label className="block">
                          <span className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">Font</span>
                          <select data-testid={`typography-lang-font-${role.key}`} value={values[role.key] || ""} onChange={(e) => set(role.key, e.target.value)}
                            className="w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" style={{ fontFamily: `'${values[role.key]}', ${scriptFallbackFor(mode)}` }}>
                            <option value="">Default (browser)</option>
                            <optgroup label="Script fonts">{opts.map((f) => <option key={f} value={f} style={{ fontFamily: `'${f}'` }}>{f}</option>)}</optgroup>
                            {custom.length > 0 && <optgroup label="Your fonts">{custom.map((f) => <option key={f.id} value={f.family} style={{ fontFamily: `'${f.family}'` }}>{f.family}</option>)}</optgroup>}
                          </select>
                        </label>
                        <label className="block">
                          <span className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">Size</span>
                          <select data-testid={`typography-lang-size-${role.key}`} value={values[`${role.key}_size`] || ""} onChange={(e) => set(`${role.key}_size`, e.target.value)}
                            className="w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none">
                            {SIZE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                        </label>
                        <div>
                          <span className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">Colour</span>
                          <div className="flex items-center gap-1.5">
                            <input type="color" data-testid={`typography-lang-color-${role.key}`} value={color || "#2c2621"} onChange={(e) => set(`${role.key}_color`, e.target.value)} className="h-9 w-9 cursor-pointer border border-line bg-parchment p-0.5" title="Pick a colour" />
                            {color
                              ? <button onClick={() => set(`${role.key}_color`, "")} title="Clear colour" className="inline-flex h-9 w-8 items-center justify-center border border-line text-ink-3 hover:border-ink hover:text-ink"><X className="h-3.5 w-3.5" /></button>
                              : <span className="text-[11px] text-ink-3">Default</span>}
                          </div>
                        </div>
                      </div>
                      <p className="mt-3 truncate border-t border-line pt-2.5 text-ink" style={lStyle(role.key)}>{sampleFor(mode).body}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* ── Default (English + Devanagari) editor ── */
            <>
              {TG_GROUPS.map((group) => (
                <div key={group.group}>
                  <h2 className="font-serif-latin text-xl text-ink">{group.group}</h2>
                  <p className="mb-3 text-xs text-ink-3">{group.note}</p>
                  <div className="space-y-4">
                    {group.roles.map((role) => {
                      const opts = fontOptionsFor(role);
                      const color = values[`${role.key}_color`] || "";
                      return (
                        <div key={role.key} className="border border-line bg-surface p-4" data-testid={`typography-role-${role.key}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-serif-latin text-lg text-ink">{role.label}</p>
                              <p className="text-xs text-ink-3">{role.help}</p>
                            </div>
                            {roleChanged(role.key) && (
                              <button onClick={() => resetRole(role.key)} className="shrink-0 text-[11px] text-saffron hover:underline">Reset</button>
                            )}
                          </div>
                          <div className="mt-3 grid gap-3 sm:grid-cols-[1.5fr_1fr_auto]">
                            <label className="block">
                              <span className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">Font</span>
                              <select
                                data-testid={`typography-font-${role.key}`}
                                value={values[role.key] || ""}
                                onChange={(e) => set(role.key, e.target.value)}
                                className="w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none"
                                style={{ fontFamily: `'${values[role.key]}', serif` }}
                              >
                                <option value="">Default</option>
                                {!role.deva && <optgroup label="Serif">{LATIN_SERIF.map((f) => <option key={f} value={f} style={{ fontFamily: `'${f}'` }}>{f}</option>)}</optgroup>}
                                {!role.deva && <optgroup label="Sans-serif">{opts.filter((f) => !LATIN_SERIF.includes(f)).map((f) => <option key={f} value={f} style={{ fontFamily: `'${f}'` }}>{f}</option>)}</optgroup>}
                                {role.deva && opts.map((f) => <option key={f} value={f} style={{ fontFamily: `'${f}'` }}>{f}</option>)}
                                {custom.length > 0 && <optgroup label="Your fonts">{custom.map((f) => <option key={f.id} value={f.family} style={{ fontFamily: `'${f.family}'` }}>{f.family}</option>)}</optgroup>}
                              </select>
                            </label>
                            <label className="block">
                              <span className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">Size</span>
                              <select
                                data-testid={`typography-size-${role.key}`}
                                value={values[`${role.key}_size`] || ""}
                                onChange={(e) => set(`${role.key}_size`, e.target.value)}
                                className="w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none"
                              >
                                {SIZE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                              </select>
                            </label>
                            <div>
                              <span className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">Colour</span>
                              <div className="flex items-center gap-1.5">
                                <input
                                  type="color"
                                  data-testid={`typography-color-${role.key}`}
                                  value={color || "#2c2621"}
                                  onChange={(e) => set(`${role.key}_color`, e.target.value)}
                                  className="h-9 w-9 cursor-pointer border border-line bg-parchment p-0.5"
                                  title="Pick a colour"
                                />
                                {color
                                  ? <button onClick={() => set(`${role.key}_color`, "")} title="Clear colour" className="inline-flex h-9 w-8 items-center justify-center border border-line text-ink-3 hover:border-ink hover:text-ink"><X className="h-3.5 w-3.5" /></button>
                                  : <span className="text-[11px] text-ink-3">Default</span>}
                              </div>
                            </div>
                          </div>
                          <p className="mt-3 truncate border-t border-line pt-2.5 text-ink" style={rStyle(role.key, role.deva)}>
                            {role.deva ? "श्री गणेशाय नमः · धर्मार्थ" : "The eternal path of Dharma · Aa Bb Cc 123"}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className="border border-line bg-surface p-4">
                <p className="font-serif-latin text-lg text-ink">Fine-tuning</p>
                <p className="text-xs text-ink-3">Optional heading weight and a global reading-text scale.</p>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Heading weight</span>
                    <select data-testid="typography-heading-weight" value={values.heading_weight || ""} onChange={(e) => set("heading_weight", e.target.value)} className="w-full border border-line bg-parchment px-3 py-2.5 text-sm text-ink focus:border-saffron focus:outline-none">
                      {WEIGHTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Reading text scale</span>
                    <select data-testid="typography-reading-scale" value={values.reading_scale || "1"} onChange={(e) => set("reading_scale", e.target.value)} className="w-full border border-line bg-parchment px-3 py-2.5 text-sm text-ink focus:border-saffron focus:outline-none">
                      {SIZES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </label>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Live preview card */}
        <div className="lg:sticky lg:top-6 lg:self-start">
          <div className="border border-line bg-surface">
            <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
              <p className="overline-label text-ink-3">Live preview</p>
              {!isLangMode && (
                <div className="flex gap-1">
                  {["hi", "en"].map((l) => (
                    <button key={l} onClick={() => setPreview(l)} className={`px-2.5 py-1 text-xs transition-colors ${preview === l ? "bg-[var(--saffron)] text-white" : "border border-line text-ink-2"}`}>
                      {l === "hi" ? "हिन्दी" : "English"}
                    </button>
                  ))}
                </div>
              )}
              {isLangMode && <span className="text-xs text-ink-3">{activeLang?.native || activeLang?.label}</span>}
            </div>
            {isLangMode ? (
              <div className="space-y-3 p-6" data-testid="typography-lang-preview">
                <p style={{ ...lStyle("eyebrow"), textTransform: "uppercase", letterSpacing: "0.18em", fontSize: values.eyebrow_size ? undefined : "0.72rem", color: values.eyebrow_color || "var(--saffron)" }}>{sampleFor(mode).eyebrow}</p>
                <h2 className="leading-tight" style={{ ...lStyle("heading"), fontSize: values.heading_size ? undefined : "2rem" }}>{sampleFor(mode).heading}</h2>
                <p className="leading-[1.9]" style={lStyle("reading")}>{sampleFor(mode).body}</p>
                <p className="italic" style={{ ...lStyle("note"), fontSize: values.note_size ? undefined : "0.8125rem", color: values.note_color || "var(--ink-3)" }}>{sampleFor(mode).note}</p>
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <span className="inline-flex items-center bg-[var(--saffron)] px-4 py-2 text-white" style={lStyle("button")}>{sampleFor(mode).button}</span>
                </div>
              </div>
            ) : (
              <div className="space-y-3 p-6">
                <p style={{ ...rStyle("eyebrow", true), textTransform: "uppercase", letterSpacing: "0.18em", fontSize: values.eyebrow_size ? undefined : "0.72rem", color: values.eyebrow_color || "var(--saffron)" }}>{preview === "hi" ? "कथा" : "Katha"}</p>
                <h2 className="leading-tight" style={preview === "hi" ? rStyle("heading_deva", true) : { ...rStyle("heading", false), fontSize: values.heading_size ? undefined : "2rem" }}>
                  {preview === "hi" ? "ध्रुव की कथा" : "The Story of Dhruva"}
                </h2>
                <p style={rStyle("explainer", true)}>
                  {preview === "hi" ? "भक्ति और दृढ़ संकल्प की अमर गाथा — एक लघु परिचय।" : "An immortal tale of devotion and resolve — a short introduction."}
                </p>
                <p className="leading-[1.9]" style={preview === "hi" ? rStyle("body_deva", true) : rStyle("reading", false)}>
                  {preview === "hi"
                    ? "राजा उत्तानपाद के पुत्र ध्रुव ने वन में जाकर घोर तप किया। उनकी अटल भक्ति देखकर भगवान विष्णु प्रकट हुए।"
                    : "Dhruva went to the forest and performed intense austerity. Moved by his unwavering devotion, Lord Vishnu appeared."}
                </p>
                <p className="italic" style={{ ...rStyle("note", true), fontSize: values.note_size ? undefined : "0.8125rem", color: values.note_color || "var(--ink-3)" }}>
                  {preview === "hi" ? "संदर्भ: श्रीमद्भागवत, स्कन्ध ४ · संपादकीय टिप्पणी" : "Reference: Srimad Bhagavatam, Canto 4 · Editorial note"}
                </p>
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <span className="inline-flex items-center bg-[var(--saffron)] px-4 py-2 text-white" style={rStyle("button", true)}>{preview === "hi" ? "मार्गदर्शिका खोलें" : "Open the guide"}</span>
                </div>
                <p className="border-t border-line pt-3" style={{ ...rStyle("footer", true), fontSize: values.footer_size ? undefined : "0.8125rem", color: values.footer_color || "var(--ink-3)" }}>
                  {preview === "hi" ? "© धर्मार्थ · भारत की ज्ञान परंपराओं का द्वार" : "© Dharmartha · A gateway to the knowledge traditions of Bharat"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
